import type { ApiEnvelope, ProblemDetails } from '@orion/contracts';

/**
 * A small hand-written client for now — `packages/api-client`
 * (docs/architecture/04-repository-structure.md) generates a typed one from
 * the OpenAPI spec once that's published (M6). Every call goes through the
 * Vite dev proxy / same-site production path, so the session cookie is
 * always first-party (docs/architecture/02 § "one site").
 */
export class ApiError extends Error {
  constructor(
    public readonly problem: ProblemDetails,
    public readonly status: number,
  ) {
    super(problem.title);
  }
}

let csrfToken: string | null = null;

/** Called once a session (login or GET /auth/session) hands back a fresh
 * token (docs/architecture/05-authentication.md § 5 — synchronizer CSRF). */
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

/** For call sites that can't go through `request()` above (e.g. a
 * multipart file upload, which needs FormData rather than a JSON body) but
 * still need to attach the same CSRF header by hand. */
export function getCsrfToken(): string | null {
  return csrfToken;
}

// Endpoints a signed-out visitor is expected to call, where a 401 means
// "wrong password"/"bad token" etc., not "your session died" — these must
// never trigger the global session-expired redirect below, or a failed
// login attempt would redirect the login form to itself. `/auth/session`'s
// own 401 is excluded too: `router.tsx`'s `_app` layout route already
// redirects on it via `beforeLoad`, and firing the handler here as well
// would race that same navigation on first load, before a session even
// exists to have expired.
const UNAUTHENTICATED_REQUEST_PATHS = new Set([
  '/auth/login',
  '/auth/logout',
  '/auth/session',
  '/auth/mfa/verify',
  '/auth/invitations/accept',
  '/auth/forgot-password',
  '/auth/reset-password',
]);

/** Set once from `main.tsx` after the router exists — kept as a plain
 * callback rather than importing the router/query client here so this
 * module stays a leaf with no app-framework coupling, the same reasoning
 * `setCsrfToken` above already follows. */
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(handler: (() => void) | null) {
  onSessionExpired = handler;
}

function handleUnauthorized(path: string) {
  if (!UNAUTHENTICATED_REQUEST_PATHS.has(path)) onSessionExpired?.();
}

/** For the handful of call sites (file uploads) that use a raw `fetch`
 * instead of `request`/`getPage` above, since multipart bodies need
 * `FormData` rather than this module's JSON-only helpers — all of them hit
 * authenticated endpoints, so no `UNAUTHENTICATED_REQUEST_PATHS` check is
 * needed the way `handleUnauthorized` does for it. */
export function reportUnauthorizedResponse() {
  onSessionExpired?.();
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'include',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(csrfToken && method !== 'GET' ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) handleUnauthorized(path);
  if (res.status === 204) return undefined as T;

  const payload = (await res.json().catch(() => null)) as ApiEnvelope<T> | ProblemDetails | null;

  if (!res.ok) {
    const problem = (payload as ProblemDetails) ?? {
      type: 'about:blank',
      title: 'Something went wrong',
      status: res.status,
      code: 'unknown_error',
      errorId: 'n/a',
    };
    throw new ApiError(problem, res.status);
  }

  return (payload as ApiEnvelope<T>).data;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** List endpoints return `{ data, meta }` — this keeps both instead of
 * discarding `meta` the way the plain `api.get` helper does. */
export async function getPage<T>(path: string): Promise<{ data: T; meta?: ApiEnvelope<T>['meta'] }> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include' });
  if (res.status === 401) handleUnauthorized(path);
  const payload = (await res.json()) as ApiEnvelope<T>;
  if (!res.ok) throw new ApiError(payload as unknown as ProblemDetails, res.status);
  return { data: payload.data, meta: payload.meta };
}
